import { IntervalUnitType } from 'frontend/ui/services/IntervalInputHelperService/enum/interval-unit-type'
import { type IIntervalChunk } from 'frontend/ui/services/IntervalInputHelperService/types/i-interval-chunk'
import { IIntervalUnitTypeItem } from 'frontend/ui/services/IntervalInputHelperService/types/i-interval-unit-type-item'

export class IntervalInputHelperService {
  private intervalUnitTypeItems: IIntervalUnitTypeItem[] = [
    {
      intervalType: IntervalUnitType.Second,
      aliases: ['s', 'sec', 'secs', 'second', 'seconds'],
      shortText: 's',
      longText: {
        singular: 'second',
        plural: 'seconds',
      },
      factor: 1,
    },
    {
      intervalType: IntervalUnitType.Minute,
      aliases: ['m', 'min', 'mins', 'minute', 'minutes'],
      shortText: 'min',
      longText: {
        singular: 'minute',
        plural: 'minutes',
      },
      factor: 60,
    },
    {
      intervalType: IntervalUnitType.Hour,
      aliases: ['h', 'hour', 'hours'],
      shortText: 'h',
      longText: {
        singular: 'hour',
        plural: 'hours',
      },
      factor: 60 * 60,
    },
    {
      intervalType: IntervalUnitType.Day,
      aliases: ['d', 'day', 'days'],
      shortText: 'd',
      longText: {
        singular: 'day',
        plural: 'days',
      },
      factor: 60 * 60 * 24,
    },
    {
      intervalType: IntervalUnitType.Week,
      aliases: ['w', 'week', 'weeks'],
      shortText: 'w',
      longText: {
        singular: 'week',
        plural: 'weeks',
      },
      factor: 60 * 60 * 24 * 7,
      shouldExcludeFromOutput: true,
    },
    {
      intervalType: IntervalUnitType.Month,
      aliases: ['mo', 'month', 'months'],
      shortText: 'mo',
      longText: {
        singular: 'month',
        plural: 'months',
      },
      factor: 60 * 60 * 24 * 30,
    },
    {
      intervalType: IntervalUnitType.Year,
      aliases: ['y', 'year', 'years'],
      shortText: 'y',
      longText: {
        singular: 'year',
        plural: 'years',
      },
      factor: 60 * 60 * 24 * 365,
    },
  ]

  public getChunksOutOfText(text: string): IIntervalChunk[] {
    const chunks: IIntervalChunk[] = []
    const regex = /(\d+)\s*([a-zA-Z]+)/g
    let match
    while ((match = regex.exec(text)) !== null) {
      const value = parseInt(match[1])
      const unit = match[2]
      const { factor, intervalType } = this.getIntervalUnitTypeItemForUnit(unit)
      const startsAt = match.index
      const endsAt = startsAt + match[0].length
      const numberStartsAt = startsAt
      const numberEndsAt = startsAt + match[1].length
      chunks.push({
        value,
        factor,
        intervalType,
        startsAt,
        endsAt,
        unit,
        numberStartsAt,
        numberEndsAt,
      })
    }
    return chunks
  }

  public getIntervalUnitTypeItemForUnit(unit: string): IIntervalUnitTypeItem {
    for (const intervalUnitTypeItem of this.intervalUnitTypeItems) {
      if (intervalUnitTypeItem.aliases.includes(unit)) {
        return intervalUnitTypeItem
      }
    }
    return this.intervalUnitTypeItems.find(item => item.intervalType === IntervalUnitType.Minute)
  }

  public convertIntervalTextToSeconds(text: string, step = 1): number | null {
    if (!text) {
      return null
    }
    if (!Number.isNaN(Number(text))) {
      return Math.round((Number(text) * 60) / step) * step
    }
    const chunks = this.getChunksOutOfText(text)
    let seconds = 0
    for (const chunk of chunks) {
      seconds += chunk.value * chunk.factor
    }
    return Math.round(seconds / step) * step
  }

  public convertSecondsToIntervalText(
    seconds: number | null,
    allowToDisplay: IntervalUnitType[],
    useLongText = false,
  ): string {
    if (!seconds && seconds != 0) {
      return ''
    }
    const chunks = []
    const sortedIntervalUnitTypeItems = this.intervalUnitTypeItems
      .filter(item => {
        if (allowToDisplay?.length) {
          if (!allowToDisplay.includes(item.intervalType)) {
            return false
          }
        }
        return !item.shouldExcludeFromOutput
      })
      .sort((a, b) => b.factor - a.factor)
    if (seconds === 0) {
      if (useLongText) {
        return `0 ${
          sortedIntervalUnitTypeItems[sortedIntervalUnitTypeItems.length - 1].longText.plural
        }`
      } else {
        return `0 ${sortedIntervalUnitTypeItems[sortedIntervalUnitTypeItems.length - 1].shortText}`
      }
    }
    const notEmptyIntervalUnitTypeItems = []
    for (const { shortText, longText, factor, intervalType } of sortedIntervalUnitTypeItems) {
      const value = Math.floor(seconds / factor)
      if (value > 0) {
        notEmptyIntervalUnitTypeItems.push(intervalType)
        if (useLongText) {
          chunks.push(`${value} ${longText[value == 1 ? 'singular' : 'plural']}`)
        } else {
          chunks.push(`${value} ${shortText}`)
        }
        seconds -= value * factor
      }
    }
    return chunks.join(' ')
  }

  convertFromUnitToSeconds(value: number | null, intervalUnitType: IntervalUnitType): number {
    if (value === null) {
      return null
    }
    const intervalUnit = this.intervalUnitTypeItems.find(
      item => item.intervalType === intervalUnitType,
    )
    return value * intervalUnit.factor
  }

  convertFromSecondsToUnit(seconds: number, intervalUnitType: IntervalUnitType): number {
    if (seconds === null) {
      return null
    }
    const intervalUnit = this.intervalUnitTypeItems.find(
      item => item.intervalType === intervalUnitType,
    )
    return seconds / intervalUnit.factor
  }
}
